
import {distinctUntilChanged} from 'rxjs/operators';
import { Observable ,  BehaviorSubject } from 'rxjs';
import { INode, NodeType, LimitDisplayedSubNodes } from './INode';
import { IAttribute } from '../attribute/IAttribute';
import { Attribute } from '../attribute/Attribute';
import { IAction } from '../action/IAction';
import { Action } from '../action/Action';

export class Node implements INode {

    public static nextLocalIdx: number = 1;

    public type: NodeType;
    public objectData: Object;
    public subNodes: Array<INode>;
    public parent: INode;
    public structure: Object;
    public attributes: Array<IAttribute> = [];
    public actions: Array<IAction> = [];
    public localIdx: number = 0;
    public shouldApplyLimit: boolean = false;
    public sortOrder: Object;
    public subNodesInitialLength = 0;
    public index: number;

    private _isUsed: BehaviorSubject<boolean>;
    private _isValid: BehaviorSubject<boolean>;
    private _isLocal: BehaviorSubject<boolean>;
    private _isFilterApplied: BehaviorSubject<boolean>;
    private _name: BehaviorSubject<string>;
    private _isAttributesModified: BehaviorSubject<boolean>;
    private _isExpanded: BehaviorSubject<boolean>;
    private _sortOrderChanged: BehaviorSubject<boolean>;

    filterCondition: Object; // filter conditions for this node in the tree view
    maxDisplayedSubNodes: number = 0; // limit of subnodes to display (0=no limitation)
    currentFilters: Object;
    private initialAttributes: Array<IAttribute>; // the minimum attributes needed to retrieve data from for successful node display

    constructor(type: NodeType, parent?: INode, structure?: Object, objectdata?: Object, index?: number, isLocal?: boolean) {
        this.parent = parent;
        this.structure = structure;
        this.type = type;
        this.sortOrder = {};
        this.index = index;

        this._isUsed = new BehaviorSubject(true);
        this._isValid = new BehaviorSubject(true);
        this._isLocal = new BehaviorSubject(false);
        this._isAttributesModified = new BehaviorSubject(false);
        this._isFilterApplied = new BehaviorSubject(false);
        this._isExpanded = new BehaviorSubject(false);
        this._name = new BehaviorSubject('Node');
        this._sortOrderChanged = new BehaviorSubject(false);

        // When instanciating a Node object, we only create all the attributes as described in the model for classes and OneInstance objects
        // This is because class object's attributes will be used by it's subNodes object
        if (this.parent === undefined || this.isClassNode || this.isOneInstance || isLocal) {
            this.createAttributes();
        } else {
            this.createMinimumAttributes();
        }
        this.createActions();
        this.addChangeListeners();
        this.updateData(objectdata);
        this.updateName();
    }

    public updateName() {
        if (this.isObjectNode) {
            if (!!this.structure && this.structure.hasOwnProperty('id')) {
                const attr = this.getAttributeByName(this.structure['id']);
                this.updateObjectNodeName(attr);
            }
        } else {
            this.updateClassNodeName();
        }
    }

    public updateClassNodeName() {
        if (this.parent && this.parent['structure']) {
            let parentSubClasses = this.parent['structure']['subClasses'];
            this.name = Object.keys(parentSubClasses).find(key => parentSubClasses[key] === this.structure);
        }
    }

    public updateObjectNodeName(attribute: IAttribute) {
        this.name = attribute ? attribute.getStringValue() : '';
    }

    public get isOneInstance(): boolean {
        return !!this.structure && this.structure.hasOwnProperty('id') && this.structure['id'] === 'One_Instance';
    }

    public get isGettable(): boolean {
        return !!this.structure && !!this.structure['get'];
    }

    public get isSettable(): boolean {
        return !!this.structure && !!this.structure['set'];
    }

    public get isCreatable(): boolean {
        return !!this.structure && !!this.structure['create'];
    }

    public get isDeletable(): boolean {
        return !!this.structure && !!this.structure['delete'];
    }

    public get isObjectNode(): boolean {
        return this.type === NodeType.Object;
    }

    public get isClassNode(): boolean {
        return this.type === NodeType.Class;
    }

    // OBSERVABLE PROPERTIES

    // Property isUsed: boolean
    get isUsed(): boolean {
        return this._isUsed.getValue();
    }

    set isUsed(isUsed: boolean) {
        this._isUsed.next(isUsed);
    }

    public isUsedChanges(): Observable<boolean> {
        return this._isUsed.asObservable().pipe(distinctUntilChanged());
    }

    // Property isLocal: boolean
    get isValid(): boolean {
        return this._isValid.getValue();
    }

    set isValid(isValid: boolean) {
        this._isValid.next(isValid);
    }

    public isValidChanges(): Observable<boolean> {
        return this._isValid.asObservable().pipe(distinctUntilChanged());
    }

    get isFilterApplied(): boolean {
        return this._isFilterApplied.getValue();
    }

    set isFilterApplied(isFilterApplied: boolean) {
        this._isFilterApplied.next(isFilterApplied);
    }

    public isFilterAppliedChanges(): Observable<boolean> {
        return this._isFilterApplied.asObservable();
    }

    public sortOrderChanges(): Observable<boolean> {
        return this._sortOrderChanged.asObservable();
    }

    // Property isLocal: boolean
    get isLocal(): boolean {
        return this._isLocal.getValue();
    }

    set isLocal(isLocal: boolean) {
        this._isLocal.next(isLocal);
    }

    public isLocalChanges(): Observable<boolean> {
        return this._isLocal.asObservable().pipe(distinctUntilChanged());
    }

    // Property isExpanded: boolean
    get isExpanded(): boolean {
        return this._isExpanded.getValue();
    }

    set isExpanded(isUsed: boolean) {
        this._isExpanded.next(isUsed);
    }

    public isExpandedChanges(): Observable<boolean> {
        return this._isExpanded.asObservable().pipe(distinctUntilChanged());
    }

    // Property name: string
    get name(): string {
        return this._name.getValue();
    }

    set name(name: string) {
        this._name.next(name);
    }

    public nameChanges(): Observable<string> {
        return this._name.asObservable().pipe(distinctUntilChanged());
    }

    /**
     * Method determines is attributes of a given Node have been modified.
     * This method, unlike isModified, only search for modification at attributes level.
     * @readonly
     * @type {boolean} true if something has been modified, false otherwise
     * @memberOf Node
     */
    get isAttributesModified(): boolean {
        return this._isAttributesModified.getValue();
    }

    set isAttributesModified(isAttributesModified: boolean) {
        this._isAttributesModified.next(isAttributesModified);
    }

    public isAttributesModifiedChanges(): Observable<boolean> {
        return this._isAttributesModified.asObservable().pipe(distinctUntilChanged());
    }

    /**
     * Method determines whether a Node or any of it's child subNodes is modified.
     * @readonly
     * @type {boolean} true if something has been modified, false otherwise
     * @memberOf Node
     */
    public get isModified(): boolean {
        let isNodeModified = this.isAttributesModified;
        if (this.subNodes && this.subNodes.length > 0) {
            isNodeModified = isNodeModified || this.subNodes.some(subNode => subNode.isModified);
        }
        return isNodeModified;
    }

    public get actionNames(): Array<string> {
        return !!this.actions ? this.actions.map(action => action.name) : [];
    }

	/**
     * Gets the list of attributes that can be used for filtering purpose, it contains at least the attribute that is the ID
     *
     * @returns {Array<IAttribute>} the list of attributes that can be used for filtering purpose
     */
    public getFilteringAttributes(limit = 0): Array<IAttribute> {
        let filteringAttributes = this.attributes.filter((attr: IAttribute) => (attr.isFiltering || attr.name === this.structure['id']) && !attr.isMask);
        if (limit > 0) {
            filteringAttributes = filteringAttributes.slice(0, limit);
        }
        return filteringAttributes;
    }

    /**
     * The minimum attributes needed to retrieve data from for this node in order to keep the payload to a minimum.
     *
     * @returns {Array<IAttribute>} the minimum attributes needed to retrieve data from
     */
    public getInitialAttributesToRetrieve(): Array<IAttribute> {
        return this.initialAttributes || this.attributes;
    }

    private updateMinimumAttributes(attributes?: Array<IAttribute>): void {

        if (this.parent === undefined || this.isClassNode || this.isOneInstance || this.isLocal) {

            // Get the list of attributes that can be used for filtering purpose, it contains at least the attribute that is the ID
            let filteringAttributes: Array<IAttribute> = attributes ? attributes : this.getMinimumFilteringAttributes();

            let extraAttributes: Array<IAttribute> = [];
            if (filteringAttributes.length === 1) {
                // if there is only one attribute shown (ie. the ID), we add the first attribute in list that is not a Password or a Set_Of
                let otherAttributes = this.attributes.filter((attr: IAttribute) => filteringAttributes[0] !== attr && !attr.isMask && !attr.isPassword && !attr.isSetOf);
                if (otherAttributes.length > 0) {
                    if (otherAttributes.every((attr: IAttribute) => attr.isUsedWhen)) {
                        // all attributes have a "used_when" condition: take all of them as extra attributes
                        extraAttributes = otherAttributes;
                    } else {
                        // prefer the attribute(s) that have not a used_when condition
                        otherAttributes = otherAttributes.filter((attr: IAttribute) => !attr.isUsedWhen);
                        // prefer an attribute that is not a Boolean, a ByteString or a Sequence
                        let otherAttribute = otherAttributes.find((attr: IAttribute) => !attr.isBoolean && !attr.isByteString && !attr.isSequence);
                        if (otherAttribute) {
                            // take the first attribute that is not a Boolean, a ByteString or a Sequence
                            extraAttributes.push(otherAttribute);
                        } else {
                            // take first attribute whatever it is
                            extraAttributes.push(otherAttributes.shift());
                        }
                    }
                }
            }

            // Recursively add attributes linked by the used_when condition
            filteringAttributes.concat(extraAttributes).forEach(attr => extraAttributes = extraAttributes.concat(this.getLinkedAttributes(attr)));

            // remove duplicates
            let minimumAttributes = Array.from(new Set(filteringAttributes.concat(extraAttributes)));

            if (!attributes) { // Do not sort when attributes have been given in method parameter (by filter component)
                // Sort the initial attributes to follow the model order
                minimumAttributes.sort((a: IAttribute, b: IAttribute) => {
                    let aIndex = this.attributes.indexOf(a);
                    let bIndex = this.attributes.indexOf(b);
                    if (aIndex < bIndex) {
                        return -1;
                    }
                    if (aIndex > bIndex) {
                        return 1;
                    }
                    return 0;
                });
            }
            // set the initial attributes
            this.initialAttributes = minimumAttributes;
        } else {
            //object node
            this.initialAttributes = this.parent.getInitialAttributesToRetrieve().map((parentAttribute) => {
                return this.getAttributeByName(parentAttribute.name);
            });
        }
    }

    public getMinimumFilteringAttributes(limit = 14): Array<IAttribute> {
        let displayableAttributes: Array<IAttribute> = [];
        let count = 0;
        for (let attr of this.getFilteringAttributes(0)) {
            displayableAttributes.push(attr);
            if (!attr.structure.hasOwnProperty('used_when')) {
                count++;
            }
            if (count === limit) {
                break;
            }
        };
        return displayableAttributes;
    }

    /**
     * Recursively gets the attributes linked by the used_when condition of the given attribute.
     *
     * @param {IAttribute} attribute the attribute eventually linked by a used_when condition
     * @returns {Array<IAttribute>} the attributes linked by the used_when condition, otherwise empty
     */
    private getLinkedAttributes(attribute: IAttribute): Array<IAttribute> {
        let linkedAttributes = [];
        if (attribute.structure.hasOwnProperty('used_when')) {
            let used_when = jsonQ(attribute.structure['used_when']);
            let attrNameArray = Array.from(new Set(used_when.find('var').value()));

            attrNameArray.forEach((attrName: string) => {
                let attr = this.getAttributeByName(attrName);
                if (attr) {
                    linkedAttributes.push(attr);
                    this.getLinkedAttributes(attr);
                }
            });
        }
        return linkedAttributes;
    }

    /**
     * Method to transform into an object all data of attribute given in parameter
     *
     * @private
     * @param {IAttribute} attribute: attribute to transform
     * @returns {Object} Object containing all data from attribute given as parameter with localValue of each data when available (otherwise, the value is used)
     *
     * @memberOf Node
     */

    public getAttributeData(attr: IAttribute): Object {
        let objData = {};
        // The storedValue for a sequence can be either the value or the localvalue as all elements inside a sequence should be sent
        let storedValue = attr.value;
        if (attr.localValue !== undefined) {
            storedValue = attr.localValue;
        }

        if (attr.isSetOf) {
            if (attr.localValue) {
                // There is at least one local value: we must build the complete set of items
                // we exclude items to delete and items containing only undefined values
                // Note: attr.localValue is an array of sub-attributes's values
                objData = attr.localValue.filter((item, i) => (attr.value[i] && !attr.value[i].toDelete && (item !== undefined && Object.keys(item).find((key) => item[key] !== undefined))));
            } else {
                //attr.value is an array of sub-attributes : we must transform this array of sub-attributes into array of sub-attribute's values
                objData = attr.value.map(subattr => {
                    if (Array.isArray(subattr.value)) {
                        return this.getData(subattr.value);
                    } else {
                        return subattr.value;
                    }
                });
            }
        } else if (attr.isSequence) {
            let objSubData = {};
            objSubData = this.getData(attr.value);
            objData = objSubData;
        } else if (attr.isByteString && attr.localValue === undefined) {
            objData = Object.keys(attr.value).map(key => attr.value[key]).join('');
        } else {
            objData = storedValue;
        }
        return objData;
    }

    /**
     * Method to transform into an object all data of attributes array given in parameter
     *
     * @private
     * @param {Array<IAttribute>} attributes: array of attributes to transform
     * @returns {Object} Object containing all data from attributes array given as parameter wih localValue of each data when available (otherwise, the value is used)
     *
     * @memberOf Node
     */
    private getData(attributes: Array<IAttribute>): Object {
        let objData = {};
        attributes.forEach((attr => {
            objData[attr.name] = this.getAttributeData(attr);
        }));
        return objData;
    }

    public getLocalData(attributes: Array<IAttribute>): Object {
        let objData = {};

        attributes.forEach((attr => {
            if (attr.isSetOf && attr.localValue !== undefined) {
                //there is at least one local value: we must send the complete set of items :
                //we exclude items to delete and items containing only undefined values
                objData[attr.name] = attr.localValue.filter((item, i) => (attr.value[i] && !attr.value[i].toDelete && (item !== undefined && Object.keys(item).find((key) => item[key] !== undefined))));
            } else if (attr.isSequence && attr.localValue !== undefined) {
                let objSubData = {};
                objSubData = this.getData(attr.value);
                objData[attr.name] = objSubData;
            } else if (Array.isArray(attr.value)) {
                // TODO: check if still usefull as we now have attr.isSequence
                let obj = this.getLocalData(attr.value);
                if (Object.keys(obj).length !== 0) {
                    objData[attr.name] = obj;
                }
            } else if (attr.localValue !== undefined) {
                objData[attr.name] = attr.localValue;
            }
        }));

        return objData;
    }

    public getPath(): string {
        let path = this.getNodePath().map((node) => { return encodeURIComponent(node.name); }).join('/');
        return this.getNodePath().map((node) => { return encodeURIComponent(node.name); }).join('/');
    }

    public getNodePath(): Array<INode> {
        if (this.parent) {
            const path = this.parent.getNodePath();
            path.push(this);
            return path;
        } else {
            return [this];
        }
    }

    public getAttributeByName(attriName: string): IAttribute {
        /* if (attriName === "Voice_Mail_Box_Sympac")
            {
            console.log("hi");
            console.log(this.attributes.find((attr) => attr.name === attriName));
            } */
        return this.attributes.find((attr) => attr.name === attriName);
    }

    private addChangeListeners() {
        // EVENT LISTENERS
        this.attributes.forEach(attribute => {
            attribute.isModifiedChanges().subscribe((isModified: boolean) => {
                if (isModified) {
                    this.isAttributesModified = true;
                } else {
                    this.isAttributesModified = this.attributes.some(attr => attr.isModified);
                }
            });

            if (this.isObjectNode && attribute.isId) {
                attribute.localValueChanges().subscribe(() => {
                    this.updateObjectNodeName(attribute);
                });
                attribute.valueChanges().subscribe(() => {
                    this.updateObjectNodeName(attribute);
                });
            }
        });
    }

    private createAttributes(): void {
        if (this.structure && this.structure['attributes']) {
            let toBeSorted = false; // tells if the attributes have to be sorted in order to follow the model order
            for (let key in this.structure['attributes']) {
                if (this.structure['attributes'].hasOwnProperty(key)) {
                    let attrStructure = this.structure['attributes'][key];
                    let attr = this.getAttributeByName(key);
                    if (!attr) {
                        attr = Attribute.create(key, attrStructure, this.isSettable, key === this.structure['id']);
                        this.attributes.push(attr);
                        toBeSorted = true;
                    }
                }
            }
            // For Node objects, all attributes are not created when the node is created.
            // The parent class object's attributes list will be sued to get the model order
            if (toBeSorted && this.parent !== undefined && !this.isClassNode && !this.isOneInstance) {
                // Sort the attributes to follow the model order
                this.attributes.sort((a: IAttribute, b: IAttribute) => {
                    let aIndex = this.parent.attributes.indexOf(this.parent.getAttributeByName(a.name));
                    let bIndex = this.parent.attributes.indexOf(this.parent.getAttributeByName(b.name));
                    if (aIndex < bIndex) {
                        return -1;
                    }
                    if (aIndex > bIndex) {
                        return 1;
                    }
                    return 0;
                });
            }
        }

        // Set the attributes that have to be retrieved initially for the node, in order to keep the payload to a minimum
        this.updateMinimumAttributes();
    }

    private createMinimumAttributes(): void {
        for (let attr of this.parent.getInitialAttributesToRetrieve()) {
            let attribute = Attribute.create(attr.name, attr.structure, this.isSettable, attr.isId);
            this.attributes.push(attribute);
        }
    }

    public updateAttributes(objectData: Object): void {
        this.attributes.forEach(attr => {
            let key = attr.name;
            if (objectData && objectData.hasOwnProperty(key)) {
                this.updateAttribute(attr, objectData[key]);
            }
        });
    }

    private updateAttribute(attribute: IAttribute, value: any): void {
        if (attribute.isSetOf) {
            let nbItemsOnServer = 0;
            for (let index in value) {
                if (value.hasOwnProperty(index)) {
                    nbItemsOnServer++;
                    let existingAttr = attribute.value[index];
                    if (!existingAttr) {
                        existingAttr = Attribute.create(undefined, { type: attribute.structure['type']['set_of'] }, attribute.isSettable);
                        attribute.value.push(existingAttr);
                    }
                    this.updateAttribute(existingAttr, value[index]);
                }
            }
            //remove item(s) with index >= nbItemsOnServer, except if the item is local
            attribute.value = attribute.value.filter((subattr, index) => index < nbItemsOnServer || subattr.isLocal);
        } else if (attribute.isSequence) {
            for (const key in value) {
                if (value.hasOwnProperty(key)) {
                    let existingAttr = attribute.value.find(attr => attr.name === key);
                    if (!existingAttr && attribute.structure['type']['sequence'].hasOwnProperty(key)) {
                        //create the attribute only if the key exists in the structure model of this sequence type
                        existingAttr = Attribute.create(key, attribute.structure['type']['sequence'][key], attribute.isSettable);
                        attribute.value.push(existingAttr);
                    }
                    if (existingAttr) {
                        this.updateAttribute(existingAttr, value[key]);
                    }
                }
            }
        } else {

                attribute.value = value;

            if (attribute.localValue === value) {
                attribute.clearLocalChanges();
            }
        }
    }

    getKeyByValue(object, value):string {
        return Object.keys(object).find(key => object[key].toLocaleLowerCase() === value.toLocaleLowerCase());
    }

   /*   public updateAttributesWithLocal(objectData: Object,dict): void{
        this.attributes.forEach(attr => {
            let key = attr.name;
            let value = objectData[key];
            if (attr.isEnumerated) {
                value = this.getKeyByValue(dict, value);
                //if (value === undefined || attr.isInteger)
                //value = objectData[key];
            }

            //console.log(value);
         if (objectData[key] != undefined && objectData[key]!= "") {
            attr.value = value;
            attr.localValue = value;
         }
        });
    } */

     public updateAttributesWithLocal(objectData: Object,dict): void{
        let names = Object.keys(objectData);
        for(let name of names){
            //console.log(name);
            console.log("trying to get attribute with name --->" + name.trim());
            let attr: IAttribute = this.getAttributeByName(name.trim());
            if (attr === undefined) {
                console.log("undefined detected for--?"+ name.trim() );
            }
            let value = objectData[name];

            if (attr.isSequence && value != undefined) {
                attr.value = value;
                attr.localValue = value;
            } else {
                if (attr.isEnumerated && value.trim() != "" && value != undefined) {
                    value = this.getKeyByValue(dict, value.trim());
                    //if (value === undefined || attr.isInteger)
                    //value = objectData[key];
                }
                if (value != undefined && value.trim() != "" && attr.value != value.trim("\n")) {
                attr.value = value.trim("\n");
                attr.localValue = value.trim("\n");
             }
            }
        }
    }

    public updateData(objData?: Object) {
        this.objectData = objData;

        this.updateAttributes(objData);
        this.evaluateSubNodesUsedWhen();
        this.evaluateAttributesUsedWhen();
    }

    public updateAndCreateMissingData(objData?: Object) {
        this.objectData = objData;
        this.createAttributes();
        this.addChangeListeners();
        this.updateAttributes(objData);
        this.evaluateSubNodesUsedWhen();
        this.evaluateAttributesUsedWhen();
    }

    /* remove server subnodes, and keep local creations and local modifications */
    public removeServerSubNodes(): void {
        if (this.subNodes) {
            this.subNodes = this.subNodes.filter((subnode) => subnode.isAttributesModified || subnode.isLocal);
        }
    }

    public copyIdlocalToValue(): void {
        if (this.structure) {
            let idAttr: IAttribute = this.getAttributeByName(this.structure['id']);
            //copy localValue (if defined) to value for id attribute
            if (idAttr && idAttr.localValue) {
                this.updateAttribute(idAttr, idAttr.localValue);
            }
        }
    }

    public evaluateUsedWhen(data: Object) {
        const usedWhenRule = this.structure['used_when'];
        if (usedWhenRule) {
            this.isUsed = jsonLogic.apply(usedWhenRule, data);
        } else {
            this.isUsed = true;
        }
    }

    public evaluateSubNodesUsedWhen() {
        if (this.subNodes && this.objectData) {
            let localData: Object = Object.assign(JSON.parse(JSON.stringify(this.objectData)), this.getData(this.attributes));
            this.subNodes.forEach((subNode: INode) => {
                subNode.evaluateUsedWhen(localData);
            });
        }
    }

    public evaluateAttributesUsedWhen() {
        if (this.attributes && this.objectData) {
            let localData: Object = Object.assign(JSON.parse(JSON.stringify(this.objectData)), this.getData(this.attributes));
            this.attributes.forEach((attr: IAttribute) => {
                attr.evaluateUsedWhen(localData);
            });
        }
    }

    public initDisplayedSubNodes(): void {
        if (this.subNodes) {
            if (this.isClassNode && !this.isOneInstance && this.maxDisplayedSubNodes === 0) {
                this.resetDisplayedSubNodesLimit();
            }
        }
    }

    public resetDisplayedSubNodesLimit(): void {
        this.maxDisplayedSubNodes = LimitDisplayedSubNodes;
        if (this.subNodes && (this.subNodesInitialLength > this.maxDisplayedSubNodes)) {
            this.shouldApplyLimit = true;
        }
    }

    /**
     * Set the filtering confdition of subNodes for this node.
     *
     * @param {filterCondition} Object containing the filtering condition to be applied on the node
     */
    public setFilterCondition(filterCondition: Object, filterIndex?: number, enabled?: boolean): void {
        if (!!filterCondition) {
            if (filterIndex !== undefined) {
                this.saveCurrentFilter(filterCondition, filterIndex, enabled !== undefined ? enabled : true);
            } else {
                this.filterCondition = filterCondition;
            }
        }
    }

    public resetFilterCondition(keepCurrentFilters = false): void {
        this.filterCondition = undefined;
        if (keepCurrentFilters) {
            //we keep the entries in currentFilter (to not lose changes in attribute names), but we mark the entries as disabled (meaining the filter is not active)
            if (this.currentFilters) {
                Object.keys(this.currentFilters).forEach(filterIndex => {
                    this.currentFilters[filterIndex]['enabled'] = false;
                });
            }
        } else {
            this.currentFilters = {};
        }
    }

    /**
     * Method to save the filterCondition used by jsonlogic in order to have a more convenient data structure for reuse
     * Based on this.filterCondition, we will create an object in array this.currentFilters that has the following structure:
     * {
     *     'name' : ,
     *     'operator' : ,
     *     'value' :
     * }
     * @private
     *
     * @memberOf Node
     */
    private saveCurrentFilter(filterCondition: Object, filterIndex: number, enabled: boolean): void {
        if (!this.currentFilters) {
            this.currentFilters = {};
        }
        for (let compoundOperator in filterCondition) {
            if (filterCondition.hasOwnProperty(compoundOperator)) {
                let rulesArray = filterCondition[compoundOperator] as Array<Object>;
                rulesArray.forEach((rule, index) => {
                    for (let logicalExpression in rule) {
                        if (rule.hasOwnProperty(logicalExpression)) {
                            let data = rule[logicalExpression] as Array<Object>;
                            this.currentFilters[filterIndex !== undefined ? filterIndex : index] = { enabled: enabled, name: data[0]['var'], operator: logicalExpression, value: data[1] };
                        }
                    }
                });
            }
        }
    }

    public setInitialAttributes(attributes: Array<IAttribute>): void {
        //ensure that the id is in the array at first position
        if (attributes && (attributes.length === 0 || !attributes[0].isId)) {
            //insert id attribute at first position
            attributes.splice(0, 0, this.getAttributeByName(this.structure['id']));
        }

        // set the initial attributes
        this.updateMinimumAttributes(attributes);
    }

    public hasSubClass(): boolean {
        if (this.structure && this.structure.hasOwnProperty('subClasses')) {
            return true;
        }
        return false;
    }

    private getAttributesValidity(attributes: Array<IAttribute>): boolean {
        return attributes.every(attr => {
            if (attr.isUsed) {
                if (attr.isSetOf || attr.isSequence) {
                    if (attr.value && Array.isArray(attr.value)) {
                        return this.getAttributesValidity(attr.value);
                    } else {
                        return true;
                    }
                } else {
                    return attr.isValid;
                }
            } else {
                return true;
            }
        });
    }

    public computeValidity(): void {
        if (this.isClassNode && this.isOneInstance && this.subNodes && this.subNodes.length > 0) {
            this.subNodes[0].computeValidity();
            this.isValid = this.subNodes[0].isValid;
        } else {
            this.isValid = this.getAttributesValidity(this.attributes);
        }
    }

    public setValidity(validity: boolean) {
        this.isValid = validity;
    }

    public clearLocalChanges(): void {
        this.attributes.forEach(attr => attr.clearLocalChanges());
        this.isValid = true;
    }

    public removeAttributesMarkedToDelete(): void {
        this.attributes.forEach(attr => attr.removeSubAttrMarkedToDelete());
    }

    public removeLocalAttributes(): void {
        this.attributes.forEach(attr => attr.removeLocalSubAttr());
    }

    public getSubNodes(): Array<INode> {
        if (this.isClassNode && this.isOneInstance && this.subNodes && this.subNodes.length > 0) {
            return this.subNodes[0].getSubNodes();
        }
        return this.subNodes;
    }

    public getDisplayedSubNodes(start?: number, end?: number): Array<INode> {
        if (this.isClassNode && this.isOneInstance && this.subNodes && this.subNodes.length > 0) {
            return this.subNodes[0].getSubNodes();
        }
        return this.subNodes ? this.subNodes.slice(start, end) : this.subNodes;
    }

    public toggleSortCriteria(attribute: IAttribute) {
        if (!this.isAttributeSorted(attribute)) {
            this.setSortCriteria(attribute, 'asc');
        } else if (this.isAttributeAscSorted(attribute)) {
            this.setSortCriteria(attribute, 'desc');
        } else if (this.isAttributeDescSorted(attribute)) {
            this.deleteSortCriteria(attribute);
        }
        this._sortOrderChanged.next(true);
    }

    public deleteSortCriteria(attribute: IAttribute) {
        let attributeName = attribute.name;
        if (attributeName !== undefined && this.sortOrder[attributeName] !== undefined)  {
            delete this.sortOrder[attributeName];
        }
    }

    public deleteAllSortCriteria() {
        this.sortOrder = {};
    }

    public getSortCriteria(attribute: IAttribute): string {
        if (!this.isAttributeSorted(attribute)) {
            return 'none';
        } else {
            return this.sortOrder[attribute.name];
        }
    }

    public setSortCriteria(attribute: IAttribute, order: string) {
        this.sortOrder[attribute.name] = order;
    }

    public isAttributeSorted(attribute: IAttribute): boolean {
        return this.sortOrder.hasOwnProperty(attribute.name);
    }

    private isAttributeAscSorted(attribute: IAttribute): boolean {
        return this.isAttributeSorted(attribute) && this.sortOrder[attribute.name] === 'asc';
    }

    private isAttributeDescSorted(attribute: IAttribute): boolean {
        return this.isAttributeSorted(attribute) && this.sortOrder[attribute.name] === 'desc';
    }

    //Actions
    private createActions(): void {
        if (this.isObjectNode && this.structure && this.structure['actions']) {
            for (let key in this.structure['actions']) {
                if (this.structure['actions'].hasOwnProperty(key)) {
                    const actionStructure = this.structure['actions'][key];
                    if (actionStructure['name']) {
                        let action = this.getActionByName(actionStructure['name']);
                        if (!action) {
                            action = Action.create(actionStructure);
                            this.actions.push(action);
                        }
                    }
                }
            }
        }
    }

    public getActionByName(name: string): IAction {
        return this.actions.find((action) => action.name === name);
    }

    public toString(): string {
        return this.getPath();
    }

    public filterApplied(): void {
        this.isFilterApplied = true;
    }

    /**
     * Method evaluate whether there is only one attribute filtered and, if it is the case, return the key of it in currentFilters
     **/
    public getSoleFilteringAttributeKey(): string {
        if (this.currentFilters && this.filterCondition) {
            if ( Object.keys(this.filterCondition['and']).length === 1 ) { // only one filter selected
                return Object.keys(this.currentFilters).find(key => this.currentFilters[key]['enabled']);
            }
        }
        return undefined;
    }
}
